import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { UiModule } from '@components/ui/ui-module';
import { Router } from '@services/route.service';
import { User as UserService } from '@services/user.service';
import { Global as Global } from '@services/global.service';
import { User, Pays, Right, Plan } from '@models/user.model';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { NotificationService } from '@services/notification.service';

@Component({
  selector: 'lgb-sign',
  imports: [
    CommonModule,
    UiModule,
    FormsModule,
    RouterModule
  ],
  templateUrl: './sign.html',
  styleUrl: './sign.css',
})
export class Sign {
  //services
  readonly router: Router=inject(Router);
  readonly route=inject(ActivatedRoute);
  readonly userService=inject(UserService);
  readonly gServ=inject(Global);
  readonly notify=inject(NotificationService)

  //data
  readonly user=signal<User>(new User());
  readonly subscriptions=signal<Plan[]>([]);
  readonly actifPlan=computed(() => this.subscriptions().find(plan => new Date(plan.end).getTime()>Date.now()));
  readonly pays=toSignal<Pays[]>(this.gServ.getPays());
  readonly rights=toSignal<Right[]>(this.gServ.getRights());

  //ui
  readonly loading=signal<boolean>(false);
  readonly msg=signal<string | null>(null);
  readonly error=signal<string | null>(null);
  readonly cgv=signal<boolean>(false);
  readonly isAdmin=signal<boolean>(false);
  readonly isSgining=computed(() => this.user().ident==null);
  readonly action=signal<'infos' | 'offre'>('infos');
  readonly img=computed(() => this.userService.getImg()+'?v='+Date.now());

  constructor(){
    //si l'utilisateur est connecté         => alors modification de l'utuisateur courant
    if(this.userService.isConnected())
      this.user.set(this.userService.user());

    //si dans la route on a un ident        => alors modification d'un utilisateur
    this.route.paramMap
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(params => {
          this.isAdmin.set(false);
          if(params.get('ident')){
            this.isAdmin.set(true);
            return this.userService.getUser(params.get('ident'));
          }
          else if(this.userService.isConnected())
            return of(this.userService.user());
          else
            return of(new User());
        }),
        tap(user => this.user.set(user)),
        switchMap(user => {
          if(!this.isSgining())
            return this.userService.getSubscriptions(user.ident);
          else
            return of(null);
        })
      )
      .subscribe({
        next: plans => {
          this.subscriptions.set(plans);
          this.loading.set(false);
        },
        error: error => {
          this.error.set(error);
          this.loading.set(false);
        }
      });
  }

  hasRight(right: string){
    return this.user().rights.filter(r => right==r).length>0;
  }

  setRight(right: string){
    if(this.hasRight(right))
      this.user().rights.splice(this.user().rights.findIndex(r => r==right), 1);
    else
      this.user().rights.push(right);
  }

  setUser(formVal: HTMLFormElement){
    this.loading.set(true);
    let data=new FormData(formVal);
    if(this.isSgining()){
      this.userService.subscribe(data)
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.notify.notifySuccess('Inscription réussie');
            this.router.navigate('login');
          },
          error: e => {
           this.loading.set(false);
           this.error.set(e);
          }
        });
    }
    else{
      this.userService.setUser(this.user().ident, data).subscribe({
        next: user => {
          if(this.user().ident==this.userService.user().ident)
            this.userService.user.set(user);
          this.msg.set('Modification réussie');
          this.loading.set(false);
        },
        error: e => {
          this.loading.set(false);
          this.error.set(e);
        }
      });
    }
  }

  unlog(){
    this.loading.set(true);
    this.userService.unlog().subscribe(() => {
      this.loading.set(false);
      this.router.navigate('login');
    });
  }
}
